import { Avatar, cx } from "@light-technology/components";
import { Cube01 } from "@untitledui/icons";
import { api } from "../state/api";
import type { AppResponse } from "../state/api.types";
import {
  ENROLLMENTS_DEFAULT_DAYS,
  STATS_DEFAULT_DAYS,
} from "../utilities/analyticsHelpers";
import {
  formatPowerDelivered,
  getPowerDeliveredMessage,
  getRecentlyActivatedAccountsMessage,
} from "../utilities/stats";
import { useDocumentTitle } from "../utilities/useDocumentTitle";
import { ActiveAccountsAreaChart } from "./common/ActiveAccountsAreaChart";
import { EnrollmentsLineChart } from "./common/EnrollmentsLineChart";
import { LocationsCard } from "./common/LocationsCard";
import { StatsCard } from "./common/StatsCard";

type AnalyticsHomePageProps = {
  app: AppResponse;
};

const RIGHT_COLUMN_CLASSNAMES =
  "rounded-xl border border-secondary flex min-h-max flex-col justify-between @page-sm:flex-1 overflow-hidden";
const RIGHT_COLUMN_STATS_CLASSNAMES = "gap-3xl p-3xl";

export const AnalyticsHomePage = (props: AnalyticsHomePageProps) => {
  useDocumentTitle(props.app.name);

  const configuration = api.useGetConfigurationQuery(props.app.uuid);
  const statsQuery = api.useGetAppStatsQuery({
    appUUID: props.app.uuid,
    days: STATS_DEFAULT_DAYS,
  });
  const powerStatsQuery = api.useGetAppPowerStatsQuery({
    appUUID: props.app.uuid,
  });

  const { value: powerAmount, unit: powerUnit } = powerStatsQuery.data
    ? formatPowerDelivered(powerStatsQuery.data.total_power_delivered_mwh)
    : { value: "-", unit: "megawatt-hours" };
  return (
    <div className="flex min-h-full flex-col p-4xl">
      <div className="mb-7xl flex items-center gap-xl">
        <Avatar
          size="lg"
          placeholderIcon={Cube01}
          src={configuration.data?.favicon_logo ?? undefined}
        />
        <div className="flex flex-col">
          <div className="text-display-sm font-semibold">
            {props.app.name} App
          </div>
          <div className="font-mono text-sm text-tertiary">
            {props.app.slug}
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-4xl @page-sm:flex-row">
        {/* Left Column: Charts - 2/3 width */}
        <div className="flex min-w-0 flex-1 flex-col gap-4xl @page-md:flex-2">
          <EnrollmentsLineChart
            appUUID={props.app.uuid}
            appSlug={props.app.slug}
            days={ENROLLMENTS_DEFAULT_DAYS}
            className="flex-1 px-0 pb-0"
          />
          <ActiveAccountsAreaChart
            appUUID={props.app.uuid}
            appSlug={props.app.slug}
            className="flex-1 px-0 pb-0"
          />
        </div>

        {/* Right Column: Stats Cards - 1/3 width */}
        <div className="flex flex-1 flex-col gap-4xl @page-sm:max-w-80 @page-md:max-w-96 @page-lg:max-w-120">
          <StatsCard
            className={cx(
              RIGHT_COLUMN_CLASSNAMES,
              RIGHT_COLUMN_STATS_CLASSNAMES,
            )}
            header="Upcoming accounts"
            number={statsQuery.data?.upcoming_accounts ?? "-"}
            helperText={
              statsQuery.data
                ? getRecentlyActivatedAccountsMessage(
                    statsQuery.data.recently_activated_accounts,
                  )
                : undefined
            }
            descriptor="scheduled for service"
            hasError={!!statsQuery.error}
            isLoading={statsQuery.isLoading}
          />

          <StatsCard
            className={cx(
              RIGHT_COLUMN_CLASSNAMES,
              RIGHT_COLUMN_STATS_CLASSNAMES,
            )}
            header="Power delivered"
            number={powerAmount}
            descriptor={powerUnit}
            helperText={
              powerStatsQuery.data
                ? getPowerDeliveredMessage(
                    powerStatsQuery.data.total_power_delivered_mwh,
                  )
                : undefined
            }
            hasError={!!powerStatsQuery.error}
            isLoading={powerStatsQuery.isLoading}
          />

          <LocationsCard
            appUUID={props.app.uuid}
            className={cx(RIGHT_COLUMN_CLASSNAMES, "gap-md pb-3xl")}
          />
        </div>
      </div>
    </div>
  );
};
